/**
 * @engine/core - Profiler
 * 
 * Performance profiling system for measuring subsystem timings.
 * Similar to Unity's Profiler - tracks CPU time per system per frame.
 * 
 * Features:
 * - Hierarchical timing (nested begin/end)
 * - Frame history for graphs
 * - Memory tracking (if available)
 * - Export to JSON/Chrome trace format
 */

/**
 * Single timing sample
 */
export interface ProfileSample {
  name: string;
  startTime: number;
  duration: number;
  depth: number;
  children: ProfileSample[];
}

/**
 * Frame profiler data
 */
export interface ProfileFrame {
  frameIndex: number;
  timestamp: number;
  totalDuration: number;
  samples: ProfileSample[];
  memory?: MemorySnapshot;
}

/**
 * Memory snapshot
 */
export interface MemorySnapshot {
  jsHeapSizeLimit?: number;
  totalJSHeapSize?: number;
  usedJSHeapSize?: number;
  wasmMemory?: number;
}

/**
 * Profiler statistics for a named section
 */
export interface ProfilerStats {
  name: string;
  callCount: number;
  totalTime: number;
  minTime: number;
  maxTime: number;
  avgTime: number;
  lastTime: number;
  history: number[];
}

/**
 * Stack entry for nested profiling
 */
interface ProfileStack {
  name: string;
  startTime: number;
  depth: number;
  children: ProfileSample[];
}

/**
 * Accumulated stats for a section
 */
interface AccumulatedStats {
  callCount: number;
  totalTime: number;
  minTime: number;
  maxTime: number;
  history: number[];
}

const MAX_HISTORY = 300; // ~5 seconds at 60fps
const MAX_FRAMES = 120;

/**
 * Profiler - Performance measurement system
 * 
 * @example
 * ```typescript
 * // In game loop
 * Profiler.beginFrame();
 * 
 * Profiler.begin('Physics');
 *   Profiler.begin('Physics.BroadPhase');
 *   // ... work
 *   Profiler.end('Physics.BroadPhase');
 *   
 *   Profiler.begin('Physics.NarrowPhase');
 *   // ... work
 *   Profiler.end('Physics.NarrowPhase');
 * Profiler.end('Physics');
 * 
 * Profiler.begin('Render');
 * // ... work
 * Profiler.end('Render');
 * 
 * Profiler.endFrame();
 * 
 * // Get stats
 * const stats = Profiler.getStats('Physics');
 * console.log(`Physics: ${stats.avgTime.toFixed(2)}ms`);
 * ```
 */
export class Profiler {
  // Singleton state
  private static _enabled: boolean = false;
  private static _frameIndex: number = 0;
  private static _frameStartTime: number = 0;
  
  // Current frame
  private static _stack: ProfileStack[] = [];
  private static _currentFrame: ProfileSample[] = [];
  
  // History
  private static _frames: ProfileFrame[] = [];
  private static _stats: Map<string, AccumulatedStats> = new Map();
  
  // Config
  private static _maxHistory: number = MAX_HISTORY;
  private static _maxFrames: number = MAX_FRAMES;
  
  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  
  /**
   * Enable/disable profiler
   */
  static setEnabled(enabled: boolean): void {
    Profiler._enabled = enabled;
  }
  
  static isEnabled(): boolean {
    return Profiler._enabled;
  }
  
  /**
   * Configure history sizes
   */
  static configure(options: {
    maxHistory?: number;
    maxFrames?: number;
  }): void {
    if (options.maxHistory !== undefined) {
      Profiler._maxHistory = options.maxHistory;
    }
    if (options.maxFrames !== undefined) {
      Profiler._maxFrames = options.maxFrames;
    }
  }
  
  /**
   * Clear all profiler data
   */
  static reset(): void {
    Profiler._frameIndex = 0;
    Profiler._stack = [];
    Profiler._currentFrame = [];
    Profiler._frames = [];
    Profiler._stats.clear();
  }
  
  // ============================================================================
  // FRAME LIFECYCLE
  // ============================================================================
  
  /**
   * Begin profiling a new frame
   */
  static beginFrame(): void {
    if (!Profiler._enabled) return;
    
    Profiler._frameStartTime = performance.now();
    Profiler._stack = [];
    Profiler._currentFrame = [];
  }
  
  /**
   * End frame profiling and store results
   */
  static endFrame(): void {
    if (!Profiler._enabled) return;
    
    // Close any open sections (shouldn't happen with correct usage)
    while (Profiler._stack.length > 0) {
      const section = Profiler._stack.pop()!;
      console.warn(`Profiler: Section '${section.name}' was not closed`);
    }
    
    const totalDuration = performance.now() - Profiler._frameStartTime;
    
    // Capture memory if available
    let memory: MemorySnapshot | undefined;
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const mem = (performance as any).memory;
      memory = {
        jsHeapSizeLimit: mem.jsHeapSizeLimit,
        totalJSHeapSize: mem.totalJSHeapSize,
        usedJSHeapSize: mem.usedJSHeapSize,
      };
    }
    
    // Store frame
    const frame: ProfileFrame = {
      frameIndex: Profiler._frameIndex,
      timestamp: Profiler._frameStartTime,
      totalDuration,
      samples: Profiler._currentFrame,
      ...(memory && { memory }),
    };
    
    Profiler._frames.push(frame);
    
    // Trim old frames
    while (Profiler._frames.length > Profiler._maxFrames) {
      Profiler._frames.shift();
    }
    
    Profiler._frameIndex++;
  }
  
  // ============================================================================
  // SECTION PROFILING
  // ============================================================================
  
  /**
   * Begin timing a section
   */
  static begin(name: string): void {
    if (!Profiler._enabled) return;
    
    const depth = Profiler._stack.length;
    
    Profiler._stack.push({
      name,
      startTime: performance.now(),
      depth,
      children: [],
    });
  }
  
  /**
   * End timing a section
   */
  static end(name: string): void {
    if (!Profiler._enabled) return;
    
    const endTime = performance.now();
    
    if (Profiler._stack.length === 0) {
      console.warn(`Profiler: end('${name}') called without matching begin()`);
      return;
    }
    
    const entry = Profiler._stack.pop()!;
    
    if (entry.name !== name) {
      console.warn(`Profiler: end('${name}') doesn't match begin('${entry.name}')`);
    }
    
    const duration = endTime - entry.startTime;
    
    const sample: ProfileSample = {
      name: entry.name,
      startTime: entry.startTime,
      duration,
      depth: entry.depth,
      children: entry.children,
    };
    
    // Add to parent or root
    if (Profiler._stack.length > 0) {
      Profiler._stack[Profiler._stack.length - 1]!.children.push(sample);
    } else {
      Profiler._currentFrame.push(sample);
    }
    
    // Update accumulated stats
    Profiler.updateStats(name, duration);
  }
  
  /**
   * Profile a function call
   */
  static measure<T>(name: string, fn: () => T): T {
    Profiler.begin(name);
    try {
      return fn();
    } finally {
      Profiler.end(name);
    }
  }
  
  /**
   * Profile an async function
   */
  static async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    Profiler.begin(name);
    try {
      return await fn();
    } finally {
      Profiler.end(name);
    }
  }
  
  // ============================================================================
  // STATISTICS
  // ============================================================================
  
  private static updateStats(name: string, duration: number): void {
    let stats = Profiler._stats.get(name);
    
    if (!stats) {
      stats = {
        callCount: 0,
        totalTime: 0,
        minTime: Infinity,
        maxTime: 0,
        history: [],
      };
      Profiler._stats.set(name, stats);
    }
    
    stats.callCount++;
    stats.totalTime += duration;
    stats.minTime = Math.min(stats.minTime, duration);
    stats.maxTime = Math.max(stats.maxTime, duration);
    stats.history.push(duration);
    
    // Trim history
    while (stats.history.length > Profiler._maxHistory) {
      stats.history.shift();
    }
  }
  
  /**
   * Get stats for a named section
   */
  static getStats(name: string): ProfilerStats | null {
    const stats = Profiler._stats.get(name);
    if (!stats) return null;
    
    return {
      name,
      callCount: stats.callCount,
      totalTime: stats.totalTime,
      minTime: stats.minTime === Infinity ? 0 : stats.minTime,
      maxTime: stats.maxTime,
      avgTime: stats.callCount > 0 ? stats.totalTime / stats.callCount : 0,
      lastTime: stats.history.length > 0 ? stats.history[stats.history.length - 1]! : 0,
      history: [...stats.history],
    };
  }
  
  /**
   * Get all tracked section names
   */
  static getSectionNames(): string[] {
    return Array.from(Profiler._stats.keys());
  }
  
  /**
   * Get all stats
   */
  static getAllStats(): ProfilerStats[] {
    return Profiler.getSectionNames().map(name => Profiler.getStats(name)!);
  }
  
  /**
   * Get recent frames
   */
  static getFrames(): ProfileFrame[] {
    return [...Profiler._frames];
  }
  
  /**
   * Get last frame
   */
  static getLastFrame(): ProfileFrame | null {
    return Profiler._frames.length > 0 
      ? Profiler._frames[Profiler._frames.length - 1]! 
      : null;
  }
  
  /**
   * Get current frame index
   */
  static getFrameIndex(): number {
    return Profiler._frameIndex;
  }
  
  // ============================================================================
  // MEMORY
  // ============================================================================
  
  /**
   * Get current memory snapshot
   */
  static getMemorySnapshot(): MemorySnapshot {
    const snapshot: MemorySnapshot = {};
    
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const mem = (performance as any).memory;
      snapshot.jsHeapSizeLimit = mem.jsHeapSizeLimit;
      snapshot.totalJSHeapSize = mem.totalJSHeapSize;
      snapshot.usedJSHeapSize = mem.usedJSHeapSize;
    }
    
    return snapshot;
  }
  
  /**
   * Track WASM memory usage
   */
  static setWasmMemory(bytes: number): void {
    // Will be captured in next endFrame()
    // Can be called from WASM bindings
  }
  
  // ============================================================================
  // EXPORT
  // ============================================================================
  
  /**
   * Export profiler data as JSON
   */
  static exportJSON(): string {
    return JSON.stringify({
      frames: Profiler._frames,
      stats: Object.fromEntries(
        Array.from(Profiler._stats.entries()).map(([k, v]) => [
          k,
          {
            callCount: v.callCount,
            totalTime: v.totalTime,
            minTime: v.minTime === Infinity ? 0 : v.minTime,
            maxTime: v.maxTime,
            avgTime: v.callCount > 0 ? v.totalTime / v.callCount : 0,
          },
        ])
      ),
    }, null, 2);
  }
  
  /**
   * Export as Chrome trace format (for chrome://tracing)
   */
  static exportChromeTrace(): string {
    const events: object[] = [];
    
    const processName = 'Game';
    const pid = 1;
    const tid = 1;
    
    // Add process/thread name
    events.push({
      name: 'process_name',
      ph: 'M',
      pid,
      args: { name: processName },
    });
    
    events.push({
      name: 'thread_name', 
      ph: 'M',
      pid,
      tid,
      args: { name: 'Main' },
    });
    
    // Add samples from all frames
    function addSamples(samples: ProfileSample[], frameOffset: number) {
      for (const sample of samples) {
        // Convert ms to microseconds
        const ts = (sample.startTime - frameOffset) * 1000;
        const dur = sample.duration * 1000;
        
        events.push({
          name: sample.name,
          cat: 'game',
          ph: 'X', // Complete event
          ts,
          dur,
          pid,
          tid,
        });
        
        if (sample.children.length > 0) {
          addSamples(sample.children, frameOffset);
        }
      }
    }
    
    for (const frame of Profiler._frames) {
      addSamples(frame.samples, frame.timestamp);
    }
    
    return JSON.stringify({ traceEvents: events });
  }
}

// ============================================================================
// DECORATORS
// ============================================================================

/**
 * Decorator to profile a method
 * @example
 * ```typescript
 * class Physics {
 *   @profile('Physics.update')
 *   update(dt: number) { ... }
 * }
 * ```
 */
export function profile(name?: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    const profileName = name ?? `${target.constructor.name}.${propertyKey}`;
    
    descriptor.value = function (...args: any[]) {
      return Profiler.measure(profileName, () => originalMethod.apply(this, args));
    };
    
    return descriptor;
  };
}

/**
 * Decorator for async methods
 */
export function profileAsync(name?: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    const profileName = name ?? `${target.constructor.name}.${propertyKey}`;
    
    descriptor.value = async function (...args: any[]) {
      return Profiler.measureAsync(profileName, () => originalMethod.apply(this, args));
    };
    
    return descriptor;
  };
}

// ============================================================================
// SCOPED PROFILER
// ============================================================================

/**
 * RAII-style profiler scope
 * @example
 * ```typescript
 * {
 *   using _ = new ProfileScope('expensive-operation');
 *   // ... work
 * } // automatically ends when leaving scope
 * ```
 */
export class ProfileScope {
  private name: string;
  private ended: boolean = false;
  
  constructor(name: string) {
    this.name = name;
    Profiler.begin(name);
  }
  
  end(): void {
    if (!this.ended) {
      Profiler.end(this.name);
      this.ended = true;
    }
  }
  
  // For 'using' statement (TC39 explicit resource management)
  [Symbol.dispose](): void {
    this.end();
  }
}
